import { useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { Alert, Tab, Tabs, Stack } from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import {
  RangePicker,
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '@cast/shared-date-ui';
import { useClustersQuery } from '@cast/shared-cluster-context-data-access';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { useTimeRangeSelection } from 'hooks/useTimeRangeSelection';
import { usePlatformUsageDetailQuery } from 'hooks/queries/organization';

import { useReportingPlatformUsageDetail } from '../_hooks/useReportingPlatformUsageDetail';
import { DailyUsageTable } from './DailyUsageTable';
import { EntitiesTable } from './EntitiesTable';
import { PlatformUsageFooter } from './PlatformUsageFooter';
import { UsageOverTimeChart } from './UsageOverTimeChart';

const MAX_SELECTED_CLUSTERS = 13;

const PLATFORM_USAGE_RANGE_QUERY_PARAM_KEYS: RangePickerQueryParamKeys = {
  fromDate: 'platform_usage_from',
  toDate: 'platform_usage_to',
  dateRangePreset: 'platform_usage_date_preset',
  timeZone: 'platform_usage_timezone',
};

const CHART_RANGE_PRESETS = [
  DateRangeFilter.ThisMonth,
  DateRangeFilter.PreviousMonth,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last30Days,
  DateRangeFilter.Last3Months,
  DateRangeFilter.Last6Months,
  DateRangeFilter.Last9Months,
  DateRangeFilter.Last12Months,
] as const;

const NUMBER_OF_DAYS_IN_13_MONTHS = 397;

const WOOP_DATA_AVAILABILITY_HINT =
  'Workload autoscaler usage data is available from January 1, 2026. Earlier periods will appear empty in the chart.';
const WOOP_FEATURE_KEYS = ['woop', 'workload_autoscaler'];

type TableTab = 'perCluster' | 'daily';

type PlatformUsageChartsAndTablesProps = {
  /**
   * Billing platform-usage feature key (phase2, woop, …).
   * Omit when `source="overview"` (Reporting temporary meter).
   */
  feature?: string;
  /**
   * `billing` — platform-usage-detail API.
   * `overview` — cost-report org overview provisioned vCPU (Reporting until BE
   * registers feature=reporting).
   */
  source?: 'billing' | 'overview';
  /** Chart LayoutCard title. Reporting uses "Provisioned vCPUs". */
  chartTitle?: string;
  /** Aggregate metric label above the chart. */
  chartTotalLabel?: string;
};

/**
 * Shared Usage-over-time chart + Per cluster / Daily tables for classic feature
 * detail and Reporting platform usage.
 */
export const PlatformUsageChartsAndTables = ({
  feature,
  source = 'billing',
  chartTitle,
  chartTotalLabel,
}: PlatformUsageChartsAndTablesProps) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TableTab>('perCluster');
  const [entityColorMap, setEntityColorMap] = useState<Map<string, string>>(
    new Map()
  );

  const { getRange } = useTimeRangeSelection({
    queryParamKeys: PLATFORM_USAGE_RANGE_QUERY_PARAM_KEYS,
    initialState: { preset: DateRangeFilter.ThisMonth },
  });

  const { from: periodFrom, to: periodTo } = useMemo(() => {
    const range = getRange();
    return {
      from: dayjs(range.from).format('YYYY-MM-DD'),
      to: dayjs(range.to).format('YYYY-MM-DD'),
    };
  }, [getRange]);

  const { clusters } = useClustersQuery();
  const clusterProviderMap = new Map<string, K8sProvider>(
    (clusters ?? []).map((c) => [c.id, c.providerType])
  );

  const isWoopFeature = !!feature && WOOP_FEATURE_KEYS.includes(feature);

  const isOverviewSource = source === 'overview';
  const billingQuery = usePlatformUsageDetailQuery({
    feature,
    periodFrom,
    periodTo,
    enabled: !isOverviewSource && !!feature,
  });
  const overviewQuery = useReportingPlatformUsageDetail({
    periodFrom,
    periodTo,
    enabled: isOverviewSource,
  });

  const isLoading = isOverviewSource
    ? overviewQuery.isLoading
    : billingQuery.isLoading;
  const isError = isOverviewSource
    ? overviewQuery.isError
    : billingQuery.isError;
  const refetch = isOverviewSource
    ? overviewQuery.refetch
    : billingQuery.refetch;
  const detail = isOverviewSource
    ? overviewQuery.detail
    : billingQuery.data?.detail;

  const handleRowsSelected = (ids: string[]) => {
    setSelectedRows(ids.slice(0, MAX_SELECTED_CLUSTERS));
  };

  if (!isLoading && isError) {
    return <FailedToLoad refresh={refetch} />;
  }
  const entities = detail?.entities ?? [];
  const unit = detail?.unit ?? '';

  const rangePicker = (
    <RangePicker
      sx={{ width: 250 }}
      filterPresets={CHART_RANGE_PRESETS}
      initialPreset={DateRangeFilter.ThisMonth}
      queryParamKeys={PLATFORM_USAGE_RANGE_QUERY_PARAM_KEYS}
      hideTimezonePicker
      allowTimeInput={false}
      maxNumberOfDays={NUMBER_OF_DAYS_IN_13_MONTHS}
      hint={
        isWoopFeature ? (
          <Alert color="advisory" size="sm" variant="filled" noIcon>
            {WOOP_DATA_AVAILABILITY_HINT}
          </Alert>
        ) : undefined
      }
    />
  );

  return (
    <Stack gap={24}>
      {(detail || isLoading) && (
        <UsageOverTimeChart
          detail={detail}
          isLoading={isLoading}
          selectedEntityIds={selectedRows}
          actions={rangePicker}
          periodFrom={periodFrom}
          periodTo={periodTo}
          onEntityColorMapChange={setEntityColorMap}
          title={chartTitle}
          totalLabel={chartTotalLabel}
        />
      )}

      <Stack gap={8}>
        <Tabs
          value={activeTab}
          onChange={(_, tab) => setActiveTab(tab as TableTab)}
          size="medium"
          variant="standard"
          fullWidth
          testId="platform-usage-table-tabs"
        >
          <Tab value="perCluster" label="Per cluster" />
          <Tab
            value="daily"
            label="Daily"
            title="Calculated once at the end of the day"
          />
        </Tabs>

        {activeTab === 'perCluster' && (
          <EntitiesTable
            entities={entities}
            entityColorMap={entityColorMap}
            clusterProviderMap={clusterProviderMap}
            unit={unit}
            showShareOfAllUsage={isOverviewSource}
            totalUsage={isOverviewSource ? detail?.totalUsage : undefined}
            isLoading={isLoading}
            isError={isError}
            refetch={refetch}
            selectedRows={selectedRows}
            onRowsSelected={handleRowsSelected}
            maxSelection={MAX_SELECTED_CLUSTERS}
          />
        )}

        {activeTab === 'daily' && (
          <DailyUsageTable
            dailyUsages={detail?.dailyUsages ?? []}
            unit={unit}
            isLoading={isLoading}
            isError={isError}
            refetch={refetch}
          />
        )}
      </Stack>

      <PlatformUsageFooter
        selectedRows={selectedRows}
        entities={entities}
        unit={unit}
        activeTab={activeTab}
      />
    </Stack>
  );
};
